import { useMemo, useState } from "react"
import {
  Route2g,
  RouteStop,
  Toll,
  findRoute2g,
  route2gEq,
} from "@/reducers/routes2gSlice"
import { RouteInfo } from "@/components/maps/RouteView"
import AdHocCommands, {
  AvoidType,
  CarRouteConfig,
  LoadType,
} from "@/api/adHocCommands"
import { RouteStopDiff } from "@/components/RouteStopList"
import { useEventCallback } from "@/hooks/use-event-callback"
import PlanRouteParser, {
  AlternativeRoute,
  OrderAnalyzeRoute,
  PerLegAlternatives,
  RouteStopWithHandle,
} from "@/lib/planRouteParser"
import XmppPubSubApi from "@/api/xmppPubSubApi"
import { useAppSelector } from "@/reducers/hooks"
import { selectLocation } from "@/reducers/locationsSelectors"
import { parseRouteErrors } from "@/lib/routeErrors"
import { withoutStopsGoneFromRoute } from "@/lib/analyzeOrderRouteStops"

export type { PerLegAlternatives } from "@/lib/planRouteParser"

export const clearRouteHandlesFromPassthroughStops = (stops: RouteStop[]) =>
  stops.map((stop, index) => {
    const prevStop = index > 0 ? stops[index - 1] : null
    const prevIsPassthrough = prevStop?.action === "passthrough"

    const shouldClearRouteHandle =
      stop.action === "passthrough" || prevIsPassthrough

    if (stop.action === "passthrough") {
      return {
        ...stop,
        route_handle: shouldClearRouteHandle ? undefined : stop.route_handle,
      }
    }

    return {
      ...stop,
      route_handle: shouldClearRouteHandle ? undefined : stop.route_handle,
      addressUpdated: true,
    }
  }) ?? []

/**
 * Puts the passthroughs of `stops` back between `routedStops`, the regular
 * stops of `stops` just routed without them, so those carry fresh route
 * handles. As when a passthrough is added, a leg that now runs through one
 * loses its handle. Undefined when there is no passthrough to put back or
 * the routed stops do not pair up with the regular ones.
 */
export const restorePassthroughs = (
  stops: RouteStop[],
  routedStops: RouteStop[],
): RouteStop[] | undefined => {
  const regularStops = stops.filter((stop) => stop.action !== "passthrough")
  if (
    regularStops.length === stops.length ||
    regularStops.length !== routedStops.length
  ) {
    return undefined
  }

  let next = 0
  return clearRouteHandlesFromPassthroughStops(
    stops.map((stop) =>
      stop.action === "passthrough" ? stop : routedStops[next++],
    ),
  )
}

/**
 * Routes through `stops`, passthroughs included, as the "Custom" route: the
 * dispatcher's own way through the stops, listed ahead of the alternatives.
 * The route plan leaves out the first stop, so it is put back.
 */
export const fetchCustomRoute = async (
  stops: RouteStop[],
  routeJid: string,
  carConfig: CarRouteConfig,
): Promise<{ stops: RouteStop[]; route: AlternativeRoute } | undefined> => {
  const parsed = PlanRouteParser.parse(
    await AdHocCommands.getRoutePlan(window.XMPP!, routeJid, stops, carConfig),
  )
  const route = parsed?.alternativeRoutes?.[0]
  if (!parsed?.stops || !route) return undefined

  return {
    stops: [stops[0], ...parsed.stops],
    route: {
      ...route,
      type: "Custom",
      routeInfo: {
        ...route.routeInfo,
        routes: [
          {
            ...route.routeInfo.routes[0],
            id: "Custom",
            label: "Custom",
            isCustom: true,
          },
          ...route.routeInfo.routes.slice(1),
        ],
      },
    },
  }
}

export const handleFetchPerLegAlternatives = async (
  stops: Route2g["stops"],
  driverJid: string,
  options?: {
    avoid: AvoidType[]
    loadType: LoadType[]
    carConfig: CarRouteConfig
  },
) => {
  const perLegResults: PerLegAlternatives[] = []

  // Fetch alternatives for each leg (segment between consecutive stops)
  for (let i = 0; i < stops.length - 1; i++) {
    const fromStop =
      i === 0
        ? stops[0]
        : {
            ...stops[i],
            route_handle: undefined,
            addressUpdated: true,
          }

    const toStop = {
      ...stops[i + 1],
      addressUpdated: true,
      route_handle: undefined,
    }

    try {
      const result = await AdHocCommands.getRoutePlan(
        window.XMPP!,
        driverJid,
        [fromStop, toStop],
        {
          ...(options?.carConfig ?? {}),
          avoid: options?.avoid,
          loadType: options?.loadType,
        },
      )

      const parsed = PlanRouteParser.parse(result)
      if (parsed?.alternativeRoutes) {
        perLegResults.push({
          fromStopIndex: i,
          toStopIndex: i + 1,
          alternatives: parsed.alternativeRoutes,
        })
      }
    } catch (err) {
      console.error(`Error fetching alternatives for leg ${i}-${i + 1}:`, err)
    }
  }
  return perLegResults
}

const NO_STOP_IDS: ReadonlySet<string> = new Set()

function useTemporaryRoute(
  driverJid: string,
  messageId: string,
  {
    alternativeRoutesEnabled = false,
    baseStopIds = NO_STOP_IDS,
  }: {
    alternativeRoutesEnabled?: boolean
    // Ids of the stops on the driver's route when the analysis started
    baseStopIds?: ReadonlySet<string>
  } = {},
) {
  const [selectedAvoid, setSelectedAvoid] = useState<AvoidType[]>([])
  const [selectedLoadType, setSelectedLoadType] = useState<LoadType[]>([])
  const [routeErrors, setRouteErrors] = useState<string[] | null>(null)

  const clearRouteErrors = useEventCallback(() => setRouteErrors(null))

  const [alternativeRoutes, setAlternativeRoutes] = useState<
    AlternativeRoute[] | undefined
  >()
  const [perLegAlternatives, setPerLegAlternatives] = useState<
    PerLegAlternatives[] | undefined
  >()
  const [temporaryRoute, setTemporaryRoute] = useState<Route2g | undefined>()
  const [temporaryRouteInfo, setTemporaryRouteInfo] = useState<
    RouteInfo | undefined
  >()
  const [isFetchingPolylines, setIsFetchingPolylines] = useState(false)
  // Track original stop IDs from driver's route before planning started
  const [originalStopIds, setOriginalStopIds] = useState<Set<string>>(new Set())
  // Track which stops have been modified during planning
  const [modifiedStopIds, setModifiedStopIds] = useState<Set<string>>(new Set())

  const currentLocation = useAppSelector(selectLocation(driverJid))
  const driverCurrentRoute = useAppSelector(findRoute2g(driverJid), route2gEq)

  // Stop IDs from the driver's actual current route in Redux (truly existing stops)
  const driverCurrentStopIds = useMemo(() => {
    if (!driverCurrentRoute?.stops) return new Set<string>()
    return new Set(
      driverCurrentRoute.stops
        .map((stop) => stop.id)
        .filter((id): id is string => id !== undefined),
    )
  }, [driverCurrentRoute])

  // A stop the driver has left since the analysis started stays out of every
  // re-plan: gps would give it a fresh id, and from then on it would pass for
  // a stop of the new order and be saved into it.
  const withoutGoneStops = (stops: RouteStop[]) =>
    withoutStopsGoneFromRoute(stops, baseStopIds, driverCurrentRoute?.stops)

  const hasPassthroughStops = (route: Route2g | undefined) => {
    return route?.stops?.some((stop) => stop.action === "passthrough")
  }

  const resetTemporaryRoute = useEventCallback(() => {
    setTemporaryRoute(undefined)
    setTemporaryRouteInfo(undefined)
    setAlternativeRoutes(undefined)
    setPerLegAlternatives(undefined)
    setModifiedStopIds(new Set())
  })

  const setInitialTemporaryRoute = useEventCallback(
    (route: Route2g, routeInfo: RouteInfo) => {
      setTemporaryRoute(route)
      setTemporaryRouteInfo(routeInfo)
      // Store original stop IDs to preserve their route_handles
      setOriginalStopIds(
        new Set(
          route.stops
            .map((stop) => stop.id)
            .filter((id): id is string => id !== undefined),
        ),
      )
    },
  )

  const replaceStopWithAlternativeLeg = useEventCallback(
    async (route_handle: string, legIndex: number) => {
      if (!temporaryRoute || !alternativeRoutes) {
        return
      }

      // Find the leg by route_handle
      const leg = uniqueAlternativeLegs.find(
        (legData) => legData.leg.route_handle === route_handle,
      )

      if (!leg) {
        console.error("Leg not found for route_handle:", route_handle)
        return
      }

      const currentStops = temporaryRoute.stops as RouteStopWithHandle[]

      // Find actual indices of non-passthrough stops in currentStops
      // This maps logical stop indices to actual array indices
      const nonPassthroughIndices: number[] = []
      currentStops.forEach((stop, index) => {
        if (stop.action !== "passthrough") {
          nonPassthroughIndices.push(index)
        }
      })

      // legIndex is the logical (non-passthrough) stop index to replace
      // This stop is the END of a segment, so we need to remove passthroughs
      // between the previous non-passthrough stop and this one
      const actualStopIndex = nonPassthroughIndices[legIndex]

      if (actualStopIndex === undefined) {
        console.error(
          "Invalid leg index:",
          legIndex,
          "non-passthrough stops count:",
          nonPassthroughIndices.length,
        )
        return
      }

      // Find the previous non-passthrough stop (START of the segment)
      const prevStopLogicalIndex = legIndex - 1
      const prevStopActualIndex =
        prevStopLogicalIndex >= 0
          ? nonPassthroughIndices[prevStopLogicalIndex]
          : -1

      // Build new stops:
      // - Keep everything up to and including the previous non-passthrough stop
      // - Add the new leg's stop (replacing the current stop and any passthroughs before it)
      // - Keep everything after the current stop
      const newStops: RouteStopWithHandle[] = [
        ...currentStops.slice(0, prevStopActualIndex + 1),
        leg.leg.stop,
        ...currentStops.slice(actualStopIndex + 1),
      ]

      const carConfig =
        (await XmppPubSubApi.fetchCarConfig(window.XMPP!, driverJid)) ?? {}

      let customRoad: OrderAnalyzeRoute | undefined = undefined

      try {
        const result = await AdHocCommands.getRoutePlan(
          window.XMPP!,
          driverJid,
          [
            {
              addressUpdated: true,
              location: {
                formattedAddress: `${currentLocation?.lat},${currentLocation?.lon}`,
              },
            },

            ...withoutGoneStops(newStops),
          ],
          {
            ...carConfig,
            avoid: selectedAvoid,
            loadType: selectedLoadType,
          },
        )

        customRoad = PlanRouteParser.parse(result)
      } catch (err) {
        console.error(`Error fetching custom route:`, err)
      }

      if (!customRoad?.alternativeRoutes?.[0]) {
        return
      }

      // Create custom route info
      const customRouteInfo: RouteInfo = {
        routes: [
          {
            ...(customRoad?.alternativeRoutes?.[0].routeInfo.routes[0] || {}),
            id: "Custom",
            label: "Custom",
            isCustom: true,
          },
        ],
      }

      const customAlternativeRoute = {
        type: "Custom",
        stops: customRoad?.stops as RouteStopWithHandle[],
        routeInfo: customRouteInfo,
        tolls: customRoad?.alternativeRoutes?.[0].tolls,
        fuel: customRoad?.alternativeRoutes?.[0].fuel,
      }

      // Remove any existing Custom route and add the new one
      const nonCustomAlternatives = alternativeRoutes.filter(
        (alt) => !alt.routeInfo.routes[0].isCustom,
      )

      // Add custom route to alternatives array (it will appear first in the table)
      setAlternativeRoutes([customAlternativeRoute, ...nonCustomAlternatives])

      // Set as selected route
      setTemporaryRoute({
        ...temporaryRoute,
        stops: customRoad?.stops,
        routeId: "Custom",
        tolls: customRoad?.alternativeRoutes?.[0].tolls,
      })
      setTemporaryRouteInfo(customRouteInfo)

      // Track alternative leg selection
      if (messageId) {
        window.analytics.track("AnalyzeOrderAlternativeLegSelected", {
          messageId,
          legIndex,
        })
      }
    },
  )

  const perLegAlternativesForMap = useMemo(
    () =>
      perLegAlternatives?.flatMap((legGroup) => {
        return legGroup.alternatives.map((alternative, altIndex) => ({
          route_handle: alternative.stops[0].route_handle,
          type: alternative.type,
          uniqueId: `leg-${legGroup.fromStopIndex}-${legGroup.toStopIndex}-${alternative.type}-${altIndex}`,
          fromStopIndex: legGroup.fromStopIndex,
          toStopIndex: legGroup.toStopIndex,
          tolls: alternative.tolls,
          fuel: alternative.fuel,
          legs: alternative.stops.map((stop) => ({
            stop,
            route_handle: stop.route_handle,
            polyline: stop.polyline,
          })),
        }))
      }),
    [perLegAlternatives],
  )

  const selectedRouteHandles = useMemo(
    () =>
      (temporaryRoute?.stops as RouteStopWithHandle[] | undefined)?.map(
        (stop) => stop.route_handle,
      ) || [],
    [temporaryRoute?.stops],
  )

  // Filter out alternatives whose stop count differs from clean alternatives (e.g., custom
  // routes with passthroughs have extra stops and their legs don't align with clean routes)
  const alternativesWithLegs = useMemo(() => {
    if (!alternativeRoutes) return undefined

    const canonicalStopCount = alternativeRoutes.find(
      (alt) => !alt.routeInfo.routes[0]?.isCustom,
    )?.stops.length

    return alternativeRoutes
      .filter(
        (alt) =>
          canonicalStopCount === undefined ||
          alt.stops.length === canonicalStopCount,
      )
      .map((alternativeRoute, index) => ({
        type: alternativeRoute.type,
        uniqueId: `${alternativeRoute.type}-${index}`,
        legs: alternativeRoute.stops.map((stop) => ({
          stop,
          route_handle: stop.route_handle,
          polyline: stop.polyline,
        })),
      }))
  }, [alternativeRoutes])

  const uniqueAlternativeLegs = useMemo(() => {
    const allLegs: Array<{
      leg: { stop: RouteStopWithHandle; route_handle: string; polyline: string }
      legIndex: number
      id: string
      routeId: string
      tolls: Toll[]
      fuel: number
    }> = []

    // Collect legs from whole-route alternatives
    if (alternativesWithLegs && alternativesWithLegs.length > 0) {
      alternativesWithLegs.forEach((alternative) => {
        alternative.legs.forEach((leg, legIndex) => {
          if (!selectedRouteHandles.includes(leg.route_handle)) {
            allLegs.push({
              leg,
              legIndex,
              id: leg.route_handle,
              routeId: alternative.type,
              tolls: [],
              fuel: 0,
            })
          }
        })
      })
    }

    // Collect legs from per-leg alternatives
    if (perLegAlternativesForMap) {
      perLegAlternativesForMap.forEach((legAlt) => {
        legAlt.legs.forEach((leg, legIndex) => {
          if (!selectedRouteHandles.includes(leg.route_handle)) {
            allLegs.push({
              leg,
              legIndex: legAlt.fromStopIndex ?? legIndex,
              id: leg.route_handle,
              routeId: legAlt.uniqueId,
              tolls: [],
              fuel: 0,
            })
          }
        })
      })
    }

    // Helper to check if a leg is between stops that already exist in the driver's current route.
    // Uses a non-custom predefined route's stops as reference since alternative legs'
    // legIndex values come from predefined routes and may not align with custom route stops.
    const referenceStops = alternativeRoutes
      ?.find((alt) => !alt.routeInfo.routes[0]?.isCustom)
      ?.stops.filter((s) => s.action !== "passthrough")

    const isLegBetweenExistingStops = (legIndex: number): boolean => {
      if (!referenceStops || driverCurrentStopIds.size === 0) return false

      // Each stop's polyline represents the segment FROM the previous stop TO this stop.
      // referenceStops[0] is the driver's current location.
      // legIndex maps directly to referenceStops index.
      const toStop = referenceStops[legIndex]
      if (!toStop?.id) return false

      const toExists = driverCurrentStopIds.has(toStop.id)
      const toIsModified = modifiedStopIds.has(toStop.id)
      if (!toExists || toIsModified) return false

      // For the first real segment (legIndex 0 or 1 depending on whether driver location
      // is included), the "from" is the driver's current location which is always existing.
      if (legIndex === 0) return true

      const fromStop = referenceStops[legIndex - 1]
      if (!fromStop?.id) return false

      // First stop (index 0) is the driver's current location - always treat as existing
      if (legIndex === 1) {
        return !modifiedStopIds.has(fromStop.id)
      }

      const fromExists = driverCurrentStopIds.has(fromStop.id)
      const fromIsModified = modifiedStopIds.has(fromStop.id)
      return fromExists && !fromIsModified
    }

    // Deduplicate by route_handle and filter out legs between unmodified original stops
    const seenHandles = new Set<string>()
    return allLegs.filter((legData) => {
      if (seenHandles.has(legData.leg.route_handle)) {
        return false
      }
      // Don't show alternative legs for segments between existing driver stops
      if (isLegBetweenExistingStops(legData.legIndex)) {
        return false
      }
      seenHandles.add(legData.leg.route_handle)
      return true
    })
  }, [
    alternativesWithLegs,
    perLegAlternativesForMap,
    selectedRouteHandles,
    alternativeRoutes,
    driverCurrentStopIds,
    modifiedStopIds,
  ])

  const handleTemporaryRouteUpdate = useEventCallback(
    async (
      updatedRoute: Route2g,
      diff: RouteStopDiff,
      options?: {
        avoid?: AvoidType[]
        loadType?: LoadType[]
      },
    ) => {
      console.dir(diff)
      setRouteErrors(null)
      const avoid = options?.avoid ?? selectedAvoid
      const loadType = options?.loadType ?? selectedLoadType

      if (messageId) {
        window.analytics.track("AnalyzeOrderRouteEdited", {
          messageId: messageId,
          routeStopsCount: updatedRoute.stops.length,
        })

        // Track passthrough point changes separately
        if (diff.hasPassthroughChanges) {
          const passthroughAdded = diff.added.filter(
            (item) => item.stop.action === "passthrough",
          ).length
          const passthroughRemoved = diff.removed.filter(
            (item) => item.stop.action === "passthrough",
          ).length

          window.analytics.track("AnalyzeOrderPassthroughChanged", {
            messageId,
            passthroughAdded,
            passthroughRemoved,
          })
        }
      }
      if (updatedRoute.stops.length === 0) {
        resetTemporaryRoute()
        return
      }

      const carConfig =
        (await XmppPubSubApi.fetchCarConfig(window.XMPP!, driverJid)) ?? {}

      if (!currentLocation) return

      const currentLocationStop = {
        addressUpdated: true,
        location: {
          formattedAddress: `${currentLocation.lat},${currentLocation.lon}`,
        },
      }

      // We should clear per leg alternatives if non passthrough stops are changed (added, removed, moved)
      // or user changed avoid or load type options
      // In such case we are resetting custom road and fetching new alternatives for all legs

      if (
        (diff.hasNonPassthroughChanges ||
          diff.hasAvoidChanges ||
          diff.hasLoadTypeChanges ||
          diff.hasOrderChanges) &&
        !diff.hasPassthroughChanges
      ) {
        const previousRoute = temporaryRoute
        const previousRouteInfo = temporaryRouteInfo
        resetTemporaryRoute()

        // Get IDs of stops that were added or modified in this update
        const currentModifiedStopIds = new Set(
          [
            ...diff.added.map((item) => item.stop.id),
            ...diff.modified.map((item) => item.stop.id),
          ].filter((id): id is string => id !== undefined),
        )

        // Update the cumulative set of modified stops
        setModifiedStopIds(
          (prev) => new Set([...prev, ...currentModifiedStopIds]),
        )

        const sanitizedStops = withoutGoneStops(
          updatedRoute.stops
            .filter((stop) => stop.action !== "passthrough")
            .map((stop) => {
              // Preserve route_handle for original driver stops that haven't been modified
              const isOriginalStop = stop.id
                ? originalStopIds.has(stop.id)
                : false
              const wasModified = stop.id
                ? currentModifiedStopIds.has(stop.id)
                : false

              // Only clear route_handle if:
              // - It's not an original stop, OR
              // - It's an original stop but was modified, OR
              // - Routing options changed (avoid/loadType/order)
              // - Reordering happened (since that can change the meaning of existing handles)
              const shouldClearHandle =
                (!isOriginalStop || wasModified || diff.hasOrderChanges) &&
                (wasModified ||
                  diff.hasAvoidChanges ||
                  diff.hasLoadTypeChanges ||
                  diff.hasOrderChanges)

              return {
                ...stop,
                route_handle: shouldClearHandle ? undefined : stop.route_handle,
                addressUpdated: shouldClearHandle || !isOriginalStop,
              }
            }) ?? [],
        )

        if (sanitizedStops.length === 0) {
          return
        }

        //Start
        setIsFetchingPolylines(true)
        try {
          const result = await AdHocCommands.getRoutePlan(
            window.XMPP!,
            driverJid,
            [currentLocationStop, ...sanitizedStops],
            {
              ...carConfig,
              avoid,
              loadType,
            },
          )

          const {
            stops,
            route,
            alternativeRoutes: newAlternatives,
          } = PlanRouteParser.parse(result) || {}
          setTemporaryRoute({
            ...updatedRoute,
            stops: stops || sanitizedStops,
            tolls: route?.tolls,
          })
          setTemporaryRouteInfo(route)

          // Only fetch alternatives when the feature is enabled
          if (alternativeRoutesEnabled) {
            const perLegAlternatives = await handleFetchPerLegAlternatives(
              [currentLocationStop, ...sanitizedStops],
              driverJid,
              { carConfig, avoid, loadType },
            )
            setPerLegAlternatives(perLegAlternatives)
            setAlternativeRoutes(newAlternatives)
          }
        } catch (err) {
          console.error("Error fetching route:", err)
          if (previousRoute) {
            setTemporaryRoute(previousRoute)
            setTemporaryRouteInfo(previousRouteInfo)
          }
          setSelectedAvoid([...selectedAvoid])
          setSelectedLoadType([...selectedLoadType])
          setRouteErrors(parseRouteErrors(err))
          return
        } finally {
          setIsFetchingPolylines(false)
        }

        // End
      } else {
        // There are only passthrough changes, so it means user is modifying passthrough stops
        // on existing custom route, or we need to creat custom road for the first time

        // Track modified stops for passthrough changes
        const currentModifiedStopIds = new Set(
          [
            ...diff.added.map((item) => item.stop.id),
            ...diff.modified.map((item) => item.stop.id),
          ].filter((id): id is string => id !== undefined),
        )
        setModifiedStopIds(
          (prev) => new Set([...prev, ...currentModifiedStopIds]),
        )

        //Start
        setIsFetchingPolylines(true)
        try {
          let sanitizedStops = updatedRoute.stops
          console.log("[DEBUG] Passthrough stops diff:", diff)
          console.log(
            "[DEBUG] Passthrough stops before sanitized:",
            updatedRoute.stops,
          )
          if (diff.modified.length > 0 || diff.added.length > 0) {
            sanitizedStops = clearRouteHandlesFromPassthroughStops(
              updatedRoute.stops,
            )
          } else if (diff.removed.length > 0) {
            if (temporaryRoute) {
              const stopAfterRemovedStop =
                temporaryRoute.stops[diff.removed[0].index + 1]
              sanitizedStops = sanitizedStops.map((stop) => ({
                ...stop,
                route_handle:
                  stop.id === stopAfterRemovedStop?.id
                    ? undefined
                    : stop.route_handle,
              }))
            }
          }
          sanitizedStops = withoutGoneStops(sanitizedStops)
          console.log(
            "[DEBUG] Sanitized stops for passthrough update:",
            sanitizedStops,
          )
          const result = await AdHocCommands.getRoutePlan(
            window.XMPP!,
            driverJid,
            [currentLocationStop, ...sanitizedStops],
            {
              ...carConfig,
              avoid,
              loadType,
            },
          )

          const {
            stops,
            route,
            alternativeRoutes: newAlternatives,
          } = PlanRouteParser.parse(result) || {}
          console.log("[DEBUG] New alternatives:", newAlternatives)
          console.log("[DEBUG] Updated route stops:", stops)
          console.log("[DEBUG] Updated route:", route)

          setTemporaryRoute({
            ...updatedRoute,
            stops: stops || sanitizedStops,
            tolls: route?.tolls,
          })
          setTemporaryRouteInfo(route)

          // Only set alternatives when the feature is enabled
          if (alternativeRoutesEnabled) {
            const customRoute = newAlternatives?.[0]

            if (!customRoute) return
            customRoute.type = "Custom"
            customRoute.routeInfo.routes[0].isCustom = true
            customRoute.routeInfo.routes[0].id = "Custom"
            customRoute.routeInfo.routes[0].label = "Custom"
            console.log(customRoute)

            const currentAlternativesWithoutCustom = (
              alternativeRoutes || []
            ).filter((alt) => !alt.routeInfo.routes[0].isCustom)

            setAlternativeRoutes([
              customRoute,
              ...currentAlternativesWithoutCustom,
            ])
          }
        } catch (err) {
          console.error("Error fetching route:", err)
          setSelectedAvoid([...selectedAvoid])
          setSelectedLoadType([...selectedLoadType])
          setRouteErrors(parseRouteErrors(err))
          return
        } finally {
          setIsFetchingPolylines(false)
        }
      }
      setSelectedAvoid(avoid)
      setSelectedLoadType(loadType)
    },
  )

  return {
    temporaryRoute,
    temporaryRouteInfo,
    setTemporaryRoute,
    setTemporaryRouteInfo,
    alternativeRoutes,
    setAlternativeRoutes,
    perLegAlternatives,
    perLegAlternativesForMap,
    setPerLegAlternatives,
    isFetchingPolylines,
    resetTemporaryRoute,
    setInitialTemporaryRoute,
    replaceStopWithAlternativeLeg,
    handleTemporaryRouteUpdate,
    selectedAvoid,
    setSelectedAvoid,
    selectedLoadType,
    setSelectedLoadType,
    selectedRouteHandles,
    alternativesWithLegs,
    uniqueAlternativeLegs,
    hasPassthroughStops,
    originalStopIds,
    routeErrors,
    clearRouteErrors,
  }
}

export default useTemporaryRoute
